import { useEffect, useState } from "react";
import StatCard from "./StatCard";
import {
    GroupIcon,
    ShootingStarIcon,
    UserIcon,
    BoxIcon,
    PlugInIcon,
    PieChartIcon,
} from "../../icons";
import { FrameIcon } from "lucide-react";
import { API_BASE_URL } from "../../utilities/constants";
import { formatAmount, handleApiCall } from "../../utilities/helper";
import { CiCircleCheck } from "react-icons/ci";

const WidgetsStats = () => {
    const [product, setProduct] = useState(null);
    const [look, setLook] = useState(null);
    const [customer, setCustomer] = useState(null);
    const [loading, setLoading] = useState(false);
    const [controller, setController] = useState(null);

    const callProductSummaryApi = async () => {
        controller?.abort();
        const newController = new AbortController();
        setController(newController);
        const { signal } = newController;
        setLoading(true);
        try {
            const response = await handleApiCall(
                `${API_BASE_URL}/api/1.0.0/dashboard/product-summary`,
                {
                    method: "GET"
                },
                null,
                signal
            );
            if (response.responseStatus == true) {
                setProduct(response.data);
            } else {
                setProduct(null);
            }
            if (response.responseCode !== 299) {
                setLoading(false);
            }
        } catch (e) {
            console.error("Error", e);
        } finally {
            setLoading(false);
        }
    };

    const callLookSummaryApi = async () => {
        controller?.abort();
        const newController = new AbortController();
        setController(newController);
        const { signal } = newController;
        setLoading(true);
        try {
            const response = await handleApiCall(
                `${API_BASE_URL}/api/1.0.0/dashboard/look-summary`,
                {
                    method: "GET"
                },
                null,
                signal
            );
            if (response.responseStatus == true) {
                setLook(response.data);
            } else {
                setLook(null);
            }
            if (response.responseCode !== 299) {
                setLoading(false);
            }
        } catch (e) {
            console.error("Error", e);
        } finally {
            setLoading(false);
        }
    };

    const callCustomerSummaryApi = async () => {
        controller?.abort();
        const newController = new AbortController();
        setController(newController);
        const { signal } = newController;
        setLoading(true);
        try {
            const response = await handleApiCall(
                `${API_BASE_URL}/api/1.0.0/dashboard/customer-summary`,
                {
                    method: "GET"
                },
                null,
                signal
            );
            if (response.responseStatus == true) {
                setCustomer(response.data);
            } else {
                setCustomer(null);
            }
            if (response.responseCode !== 299) {
                setLoading(false);
            }
        } catch (e) {
            console.error("Error", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        callProductSummaryApi();
        callLookSummaryApi();
        callCustomerSummaryApi();
    }, []);

    const totalProducts = product?.totalProductsCount || 0;
    const processedCount = product?.processedProductCount || 0;
    const failedCount = product?.processingFailedCount || 0;
    const notNormalizedCount = totalProducts - processedCount;

    const normalizedPercent = totalProducts > 0 ? (processedCount / totalProducts) * 100 : 0;
    const failedPercent = processedCount > 0 ? (failedCount / processedCount) * 100 : 0;
    const notNormalizedPercent = normalizedPercent > 0 ? Math.max(0, 100 - normalizedPercent) : 0;

    const totalLinks = (product?.totalInternalProductLinkCount || 0) + (product?.totalExternalProductLinkCount || 0);
    const internalPercent = totalLinks > 0 ? ((product?.totalInternalProductLinkCount || 0) / totalLinks) * 100 : 0;
    const externalPercent = totalLinks > 0 ? ((product?.totalExternalProductLinkCount || 0) / totalLinks) * 100 : 0;

    return (
        <div className="space-y-8 animate-in fade-in duration-700">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <StatCard
                    title="Total Customers"
                    value={(customer?.customerCount || 0).toLocaleString()}
                    icon={GroupIcon}
                />
                <StatCard
                    title="Looks (Backoffice)"
                    value={(look?.adminGeneratedAiLookCount || 0).toLocaleString()}
                    icon={FrameIcon}
                />
                <StatCard
                    title="Looks (Users)"
                    value={(look?.userGeneratedAiLookCount || 0).toLocaleString()}
                    icon={UserIcon}
                />
                <StatCard
                    title="Total Products"
                    value={(product?.totalProductsCount || 0).toLocaleString()}
                    icon={BoxIcon}
                />
                <StatCard
                    title="Processed"
                    value={(product?.processedProductCount || 0).toLocaleString()}
                    icon={CiCircleCheck}
                />
                <StatCard
                    title="Vendors"
                    value={(product?.vendorCount || 0).toLocaleString()}
                    icon={ShootingStarIcon}
                />
            </div>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <StatCard title="Processed Product Ratio" icon={PlugInIcon} value={(product?.totalProductsCount || 0).toLocaleString()}>
                    <div className="mt-6 flex flex-col space-y-4">
                        <div>
                            <div className="mb-1.5 flex items-end justify-between">
                                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Processed</span>
                                <span className="text-sm font-bold text-brand-600 dark:text-brand-400">{Math.round(normalizedPercent)}% ({processedCount})</span>
                            </div>
                            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-white/5">
                                <div
                                    className="h-full rounded-full bg-brand-500 transition-all duration-1000 ease-out"
                                    style={{ width: `${normalizedPercent}%` }}
                                />
                            </div>
                        </div>

                        <div>
                            <div className="mb-1.5 flex items-end justify-between">
                                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Failed</span>
                                <span className="text-sm font-bold text-red-600 dark:text-red-400">{formatAmount(failedPercent)}% ({failedCount})</span>
                            </div>
                            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-white/5">
                                <div
                                    className="h-full rounded-full bg-red-500 transition-all duration-1000 ease-out"
                                    style={{ width: `${failedPercent}%` }}
                                />
                            </div>
                        </div>

                        <div>
                            <div className="mb-1.5 flex items-end justify-between">
                                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Not Processed</span>
                                    <span className="text-sm font-bold text-gray-700 dark:text-gray-300">{Math.round(notNormalizedPercent)}% ({notNormalizedCount})</span>
                                </div>
                            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-white/5">
                                <div
                                    className="h-full rounded-full bg-gray-400 transition-all duration-1000 ease-out"
                                    style={{ width: `${notNormalizedPercent}%` }}
                                />
                            </div>
                        </div>
                    </div>
                </StatCard>
                <StatCard title="Links Distribution (Ratio)" icon={PieChartIcon} value={totalLinks.toLocaleString()}>
                    <div className="mt-6 flex flex-col space-y-4">
                        <div>
                            <div className="mb-1.5 flex items-end justify-between">
                                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Internal Links</span>
                                <span className="text-sm font-bold text-brand-600 dark:text-brand-400">{Math.round(internalPercent)}% ({product?.totalInternalProductLinkCount || 0})</span>
                            </div>
                            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-white/5">
                                <div
                                    className="h-full rounded-full bg-brand-500 transition-all duration-1000 ease-out"
                                    style={{ width: `${internalPercent}%` }}
                                />
                            </div>
                        </div>

                        <div>
                            <div className="mb-1.5 flex items-end justify-between">
                                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">External Links</span>
                                <span className="text-sm font-bold text-gray-700 dark:text-gray-300">{Math.round(externalPercent)}% ({product?.totalExternalProductLinkCount || 0})</span>
                            </div>
                            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-white/5">
                                <div
                                    className="h-full rounded-full bg-gray-400 transition-all duration-1000 ease-out"
                                    style={{ width: `${externalPercent}%` }}
                                />
                            </div>
                        </div>
                    </div>
                </StatCard>
            </div>
        </div>
    );
};

export default WidgetsStats;
